
import { lazy } from "react";
import NotFound from "@/pages/NotFound";

// Preload critical pages to prevent import failures
const Index = lazy(() => import("@/pages/Index"));
const About = lazy(() => import("@/pages/About"));

// Eagerly preload blog components to prevent chunking issues
const Blog = lazy(() => {
  const importPromise = import("@/pages/Blog");
  // Add error handling for chunk load failures
  importPromise.catch(err => {
    if (process.env.NODE_ENV === 'development') {
      console.error("Failed to load Blog component:", err);
    }
  });
  return importPromise;
});

const BlogPost = lazy(() => {
  const importPromise = import("@/pages/BlogPost");
  importPromise.catch(err => {
    if (process.env.NODE_ENV === 'development') {
      console.error("Failed to load BlogPost component:", err);
    }
  });
  return importPromise;
});

// Lazy load remaining components
const CaseStudies = lazy(() => import("@/pages/CaseStudies"));
const GetStarted = lazy(() => import("@/pages/GetStarted"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const MasterServicesAgreement = lazy(() => import("@/pages/MasterServicesAgreement"));
const Sitemap = lazy(() => import("@/pages/Sitemap"));
const DynamicSitemap = lazy(() => import("@/pages/DynamicSitemap"));
const Careers = lazy(() => import("@/pages/Careers"));
const HowTo = lazy(() => import("@/pages/resources/HowTo"));

// Utility to create trailing slash routes for better SEO
const createTrailingSlashRoutes = (paths: string[]): string[] => {
  return paths
    .filter(path => !path.includes(':') && path !== '/' && !path.endsWith('*'))
    .map(path => `${path}/`);
};

// List of paths that need trailing slash versions
const pathsWithTrailingSlash = [
  "/about",
  "/case-studies",
  "/blog",
  "/get-started",
  "/privacy",
  "/master-services-agreement",
  "/sitemap",
  "/careers",
  "/resources/how-to"
];

// Core application routes
export const coreRoutes = [
  {
    path: "/",
    element: <Index />
  },
  {
    path: "/about",
    element: <About />
  },
  {
    path: "/case-studies",
    element: <CaseStudies />
  },
  {
    path: "/blog",
    element: <Blog />
  },
  {
    path: "/blog/:slug",
    element: <BlogPost />
  },
  {
    path: "/get-started",
    element: <GetStarted />
  },
  {
    path: "/privacy",
    element: <Privacy />
  },
  {
    path: "/master-services-agreement",
    element: <MasterServicesAgreement />
  },
  // Add trailing slash version of master-services-agreement
  {
    path: "/master-services-agreement/",
    element: <MasterServicesAgreement />
  },
  {
    path: "/sitemap",
    element: <Sitemap />
  },
  {
    path: "/sitemap.xml",
    element: <DynamicSitemap />
  },
  {
    path: "/careers",
    element: <Careers />
  },
  {
    path: "/resources/how-to",
    element: <HowTo />
  },
  {
    path: "*",
    element: <NotFound />
  }
];

// Add trailing slash versions for better SEO
const trailingSlashPaths = createTrailingSlashRoutes(pathsWithTrailingSlash);
trailingSlashPaths.forEach(trailingPath => {
  const basePath = trailingPath.slice(0, -1); // Remove trailing slash
  const matchingRoute = coreRoutes.find(route => route.path === basePath);
  
  if (matchingRoute && !coreRoutes.some(route => route.path === trailingPath)) {
    coreRoutes.push({
      path: trailingPath,
      element: matchingRoute.element
    });
  }
});
